import React from 'react';
import { Table, Button, Form } from 'react-bootstrap';
import GeoArea from './geo-area';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const INTERVAL_OPTIONS = [
    { value: 0, label: 'No min interval' },
    { value: 1, label: '1 min' },
    { value: 2, label: '2 min' },
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
    { value: 15, label: '15 min' },
];

const GeoTable = ({
    headers,
    data,
    title,
    locations = [],
    onExport,
    selectedForMap,
    onToggleSelect,
    onSelectAll,
    onUnselectAll,
    isCleaned,
    onCleanToggle,
    minIntervalMinutes = 0,
    onMinIntervalChange,
    showCoordinates = false,
    onToggleShowCoordinates,
}) => {
    const hasMapCheckbox = selectedForMap != null && typeof onToggleSelect === 'function';
    const isEmpty = !data || data.length === 0;
    const filteredHeaders = showCoordinates ? headers : headers.filter((h) => h !== 'latitude' && h !== 'longitude');

    if (isEmpty) {
        return (
            <div style={{ marginTop: 16 }}>
                {title && <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: '0 0 12px 0' }}>{title}</p>}
                <div
                    style={{
                        padding: '32px 24px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                    }}
                >
                    <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                        No geolocation records found for the selected date range.
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9ca3af' }}>
                        Try adjusting the date range or ensure the employee has location tracking enabled.
                    </p>
                </div>
            </div>
        );
    }

    const dataHeaders = [...filteredHeaders, 'areas'];
    const displayHeaders = hasMapCheckbox ? ['Map', ...dataHeaders] : dataHeaders;
    const tableStyle = { fontSize: 13, marginBottom: 0, width: '100%', tableLayout: 'fixed' };
    const colWidth = `${100 / displayHeaders.length}%`;

    const allSelected =
        hasMapCheckbox &&
        selectedForMap &&
        data.length > 0 &&
        data.every((row) => selectedForMap.has(row._rowKey));

    return (
        <div style={{ marginTop: 16 }}>
            {(title || (hasMapCheckbox && (onSelectAll || onUnselectAll)) || onCleanToggle) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    {title && <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>{title}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {onCleanToggle && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={onCleanToggle}
                            >
                                {isCleaned ? 'Show Raw' : 'Clean'}
                            </Button>
                        )}
                        {isCleaned && onMinIntervalChange && (
                            <Form.Select
                                size="sm"
                                style={{ width: 'auto', fontSize: 13 }}
                                value={minIntervalMinutes}
                                onChange={(e) => onMinIntervalChange(Number(e.target.value))}
                                aria-label="Min interval between points"
                            >
                                {INTERVAL_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        {hasMapCheckbox && (onSelectAll || onUnselectAll) && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={allSelected ? onUnselectAll : onSelectAll}
                            >
                                {allSelected ? 'Unselect all' : 'Select all'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {/* Fixed header table */}
            <Table striped bordered hover size="sm" style={tableStyle}>
                <colgroup>
                    {displayHeaders.map((header) => <col key={header} style={{ width: colWidth }} />)}
                </colgroup>
                <thead>
                    <tr>
                        {displayHeaders.map((header) => (
                            <th key={header}>{capitalize(header)}</th>
                        ))}
                    </tr>
                </thead>
            </Table>
            {/* Scrollable body */}
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderTop: 'none' }}>
                <Table striped bordered hover size="sm" style={tableStyle}>
                    <colgroup>
                        {displayHeaders.map((header) => <col key={header} style={{ width: colWidth }} />)}
                    </colgroup>
                    <tbody>
                        {data.map((row, i) => (
                            <tr key={row._rowKey ?? i}>
                                {hasMapCheckbox && (
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedForMap.has(row._rowKey)}
                                            onChange={() => onToggleSelect(row._rowKey)}
                                            aria-label="Show on map"
                                        />
                                    </td>
                                )}
                                {dataHeaders.map((header) => (
                                    <td key={header}>
                                        {header === 'areas' ? <GeoArea coordinates={row} locations={locations} /> : row[header]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            {(onExport || onToggleShowCoordinates) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    {onToggleShowCoordinates && (
                        <Button
                            variant={showCoordinates ? 'outline-secondary' : 'secondary'}
                            size="sm"
                            onClick={onToggleShowCoordinates}
                        >
                            Coordinates
                        </Button>
                    )}
                    {onExport && (
                        <Button variant="outline-secondary" size="sm" onClick={onExport}>
                            Export to CSV
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default GeoTable;
